import {
  Option,
  DateOption,
  DefaultDateOptionsColumn,
  DefaultPeriodOptionsColumn,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import getShipmentOptions from "../../../../../../../../../../components/Shipments/hooks/getShipmentOptions";
import { SimplePeriodOptions } from "../../../../../../../../../../constants";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import { Product } from "../../../../../../../../../../models/product";
import { PurchaseOrder } from "../../../../../../../../../../models/purchase.order";
import { Supplier } from "../../../../../../../../../../models/supplier";
import { Warehouse } from "../../../../../../../../../../models/warehouse";
import ProductService from "../../../../../../../../../../services/product.service";
import PurchaseOrderService from "../../../../../../../../../../services/purchase.order.service";
import shipmentService from "../../../../../../../../../../services/shipment.service";
import SupplierService from "../../../../../../../../../../services/supplier.service";
import WarehouseService from "../../../../../../../../../../services/warehouse.service";
import { CronJobType, ReportDateType, Status } from "../../../../../../../../../../util/enums";
import { useActionReportMaintenance } from "../../../../hooks/useAction";
import { FilterByOption } from "../../../../types";
import { convertValueToSearch } from "../../../../utils";

export const useProductsFilters = () => {
  const { t: translate } = useTranslation();
  const { getAllProjects } = useActionReportMaintenance();
  const { typeOptions } = getShipmentOptions();

  const { data: projects } = getAllProjects();
  const { currentUser } = useAppState();

  const stockOptions: FilterByOption[] = [
    {
      name: translate("projectName"),
      column: "project",
      options: projects?.data?.rows?.map((project) => {
        return { label: project.name, value: String(project.id) };
      }),
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
      requiredMessage: translate("selectAtLeastOneProject"),
    },
    {
      name: `${translate("container")} #`,
      column: "containerNumber",
      onSearch: (value: string) =>
        shipmentService.getAll({
          page: 0,
          pageSize: 20,
          companyId: currentUser?.companyId,
          groupBy: JSON.stringify(["container_id"]),
          searchByOr: JSON.stringify(convertValueToSearch(value, "container_id")),
        }),
      getOptionLabel: (container: any) => container?.containerId,
      optionColumn: "containerId",
    },
    {
      name: translate("reference"),
      column: "hwab",
      onSearch: (value: string) =>
        shipmentService.getAll({
          page: 0,
          pageSize: 20,
          companyId: currentUser?.companyId,
          groupBy: JSON.stringify(["hwab"]),
          searchByOr: JSON.stringify(convertValueToSearch(value, "hwab")),
        }),
      getOptionLabel: (option: any) => option?.hwab,
      optionColumn: "hwab",
    },
    {
      name: `${translate("order")} #`,
      column: "orders",
      onSearch: (value: string) =>
        PurchaseOrderService.getAllNetwork({
          group: ["purchase_order.id"],
          companyId: currentUser?.companyId,
          include: JSON.stringify([]),
          attributes: ["id", "reference"],
          searchByOr: JSON.stringify(convertValueToSearch(value, "purchase_order_id")),
        }),
      getOptionLabel: (option: PurchaseOrder) => option?.reference || "",
      optionColumn: "reference",
    },
    {
      name: `${translate("sku")} #`,
      column: "sku",
      onSearch: (value: string) =>
        ProductService.getAll({
          companyId: currentUser?.companyId,
          attributes: ["id", "sku"],
          searchByOr: JSON.stringify(convertValueToSearch(value, "sku")),
        }),
      getOptionLabel: (option: Product) => option?.sku || "",
      optionColumn: "id",
    },
    {
      name: translate("type"),
      column: "shipmentType",
      options: typeOptions,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      onSearch: (value: string) =>
        SupplierService.getAll({
          companyId: currentUser?.companyId,
          searchByOr: JSON.stringify(convertValueToSearch(value, "name")),
          attributes: ["id", "name", "associatedCompanyId"],
        }),
      getOptionLabel: (option: Supplier) => option?.name || "",
      optionColumn: "name",
      name: translate("destination"),
      column: "destination",
    },
  ];

  const stockOnHandOptions: FilterByOption[] = [
    {
      name: translate("projectName"),
      column: "project",
      options: projects?.data?.rows?.map((project) => {
        return { label: project.name, value: project.name };
      }),
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
      requiredMessage: translate("selectAtLeastOneProject"),
    },
    {
      name: `${translate("container")} #`,
      column: "containerNumber",
      onSearch: (value: string) =>
        shipmentService.getAll({
          page: 0,
          pageSize: 20,
          companyId: currentUser?.companyId,
          searchByOr: JSON.stringify(convertValueToSearch(value, "container_id")),
        }),
      getOptionLabel: (container: any) => container?.containerId,
      requiredMessage: translate("selectAtLeastOneContainer"),
      optionColumn: "containerId",
    },
    {
      name: `${translate("warehouse")} #`,
      column: "warehouse",
      onSearch: (value: string) =>
        WarehouseService.getAll({
          page: 0,
          pageSize: 30,
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          searchByOr: JSON.stringify(convertValueToSearch(value, "name")),
        }),
      getOptionLabel: (warehouse: Warehouse) => warehouse?.name ?? "",
      optionColumn: "name",
      requiredMessage: translate("selectAtLeastOneWarehouseOrSku"),
      notRequiredWhen: ["sku", "containerNumber"],
    },
    {
      name: `${translate("sku")} #`,
      column: "sku",
      onSearch: (value: string) =>
        ProductService.getAll({
          companyId: currentUser?.companyId,
          attributes: ["id", "sku"],
          searchByOr: JSON.stringify(convertValueToSearch(value, "sku")),
        }),
      getOptionLabel: (option: Product) => option?.sku || "",
      optionColumn: "sku",
      requiredMessage: translate("selectAtLeastOneWarehouseOrSku"),
      notRequiredWhen: ["warehouse", "containerNumber"],
    },
  ];

  const stockDateOptions: DateOption[] = [
    {
      column: ReportDateType.ETA_AT_DESTINATION,
      name: translate("etaAtDestination"),
    },
    {
      column: ReportDateType.ETA_WAREHOUSE,
      name: translate("estimateArrivalWH"),
    },
    {
      column: ReportDateType.ETA_UNPACK,
      name: translate("etaUnpackDate"),
    },
    {
      column: ReportDateType.ARRIVAL_AT_DESTINATION,
      name: translate("finalPortArrival"),
    },
    {
      column: "",
      name: translate("all"),
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
    },
  ];

  const stockOnHandPeriodOptions = [
    {
      column: "",
      name: translate("all"),
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
    },
  ];

  const getProductReportFilters = (type: CronJobType) => {
    switch (type) {
      case CronJobType.STOCK:
        return stockOptions;
      case CronJobType.STOCK_ON_HAND:
      case CronJobType.STOCK_ON_HAND_DETAILED:
      default:
        return stockOnHandOptions;
    }
  };

  const getProductReportDateOptions = (type: CronJobType) => {
    switch (type) {
      case CronJobType.STOCK:
        return stockDateOptions;
      case CronJobType.STOCK_ON_HAND:
      case CronJobType.STOCK_ON_HAND_DETAILED:
      default:
        return stockOnHandPeriodOptions;
    }
  };

  const getProductReportRequiredFilters = (type: CronJobType) => {
    switch (type) {
      case CronJobType.STOCK:
        return [];
      case CronJobType.STOCK_ON_HAND:
        return [
          {
            name: "project",
            message: translate("selectAProject"),
            title: translate("projectName"),
          },
          {
            name: "warehouse",
            message: translate("selectAWarehouse"),
            title: `${translate("warehouse")} #`,
          },
          {
            name: "sku",
            message: translate("selectASKU"),
            title: `${translate("sku")} #`,
          },
          {
            name: "containerNumber",
            message: translate("selectAContainer"),
            title: `${translate("container")} #`,
          },
        ];
      case CronJobType.STOCK_ON_HAND_DETAILED:
        return [
          {
            name: "project",
            message: translate("selectAProject"),
            title: translate("projectName"),
          },
          {
            name: "containerNumber",
            message: translate("selectAContainerNumber"),
            title: `${translate("container")} #`,
          },
          {
            name: "warehouse",
            message: translate("selectAWarehouse"),
            title: `${translate("warehouse")} #`,
          },
          {
            name: "sku",
            message: translate("selectASKU"),
            title: `${translate("sku")} #`,
          },
        ];
      default:
        return [];
    }
  };

  const getProductReportPeriodOptions = (type: CronJobType) => {
    switch (type) {
      case CronJobType.STOCK:
        return SimplePeriodOptions;
      case CronJobType.STOCK_ON_HAND:
      case CronJobType.STOCK_ON_HAND_DETAILED:
      default:
        return SimplePeriodOptions;
    }
  };

  return {
    getProductReportFilters,
    getProductReportDateOptions,
    getProductReportRequiredFilters,
    getProductReportPeriodOptions,
  };
};
